// Pre-leads, browser side: what the lead agent finds is saved here first (no company, contact or deal), kept ones get
// their candidate contacts and a drafted first email, and only a replied one is converted into a CRM deal.
import type { Identifier } from "ra-core";

import {
  brochuresOf,
  buildDraftPrompt,
  buildWhyNowPrompt,
  DraftSchema,
  preLeadRow,
  rankedFromRow,
  signalsText,
  WhyNowSchema,
  writeDraft,
  type RankedLead,
  type Rejection,
  type Segment,
} from "../../../../../supabase/functions/_shared/leadAgent.ts";
import { domainOf } from "../../../../../supabase/functions/_shared/leadFilters.ts";
import {
  blocksNewPreLead,
  ownContact,
  recipientOf,
  rejectionsFrom,
  usableContacts,
} from "../../../../../supabase/functions/_shared/outreach.ts";
import type { CrmDataProvider } from "../../providers/types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { runAi, runAiJson } from "../ai";
import {
  lookupCompany,
  recentNews,
  recentSignals,
  siteExcerpt,
} from "../companyLookup";
import {
  bestRecipient,
  personaliseBody,
  senderOf,
  signatureFor,
} from "../leads/draftNote";
import {
  contactFromEmail,
  findPeople,
  receivesMail,
  type FoundContact,
} from "../leads/findContacts";
import { addLeads, agentFit } from "../leads/leads";
import { DEFAULT_SEND_AS, type Sender } from "../leads/outlook";
import type { PreLead, PreLeadMessage } from "./types";

export { recipientOf };

const now = () => new Date().toISOString();
const log = (row: PreLead) =>
  Array.isArray(row.evidence?.log) ? (row.evidence.log as string[]) : [];

/** The pre_leads row for a ranked lead; the segment snapshot carries the agent's offer text */
export const preLeadFromRanked = (
  row: RankedLead,
  runId: string,
  segment: Segment | undefined,
  offer: string,
): Partial<PreLead> =>
  preLeadRow(row, runId, segment, offer) as unknown as Partial<PreLead>;

const one = { page: 1, perPage: 1 } as const;
const byId = { field: "id", order: "ASC" } as const;

/** Save the ranked leads as pre-leads; a company that is open, recently closed, removed by the User (whenever), in the CRM
 * with a deal, or suppressed is skipped */
export const insertPreLeads = async (
  dataProvider: CrmDataProvider,
  rows: RankedLead[],
  input: {
    salesId?: Identifier;
    runId: string;
    segments: Segment[];
    offer: string;
  },
): Promise<{ added: number; skipped: string[] }> => {
  const result = { added: 0, skipped: [] as string[] };
  for (const row of rows) {
    const { company } = row.lead;
    const domain = company.domain ?? domainOf(company.website);
    const skip = async () => {
      if (domain && (await dataProvider.isSuppressed(domain))) return true;
      const { data: same } = await dataProvider.getList<PreLead>("pre_leads", {
        filter: domain ? { domain } : { company_name: company.name },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "ASC" },
      });
      if (blocksNewPreLead(same, Date.now())) return true;
      const { data: sameCompany } = await dataProvider.getList("companies", {
        filter: domain
          ? { "website@ilike": `%${domain}%` }
          : { "name@ilike": company.name },
        pagination: one,
        sort: byId,
      });
      if (!sameCompany[0]) return false;
      const { total } = await dataProvider.getList("deals", {
        filter: { company_id: sameCompany[0].id },
        pagination: one,
        sort: byId,
      });
      return !!total;
    };
    if (await skip()) {
      result.skipped.push(company.name);
      continue;
    }
    await dataProvider.create("pre_leads", {
      data: {
        ...preLeadFromRanked(
          row,
          input.runId,
          input.segments.find((s) => s.name === row.segment),
          input.offer,
        ),
        sales_id: input.salesId,
      },
    });
    result.added++;
  }
  return result;
};

const patch = async (
  dataProvider: CrmDataProvider,
  row: PreLead,
  data: Partial<PreLead>,
) =>
  (
    await dataProvider.update<PreLead>("pre_leads", {
      id: row.id,
      data,
      previousData: row,
    })
  ).data;

export const NO_WHY_NOW = "No dated why-now fact in the last year of news";

/** The why-now fact in a company's last year of news and its signals ("" when they show no need), with what was read
 * (an AI failure throws) */
export const whyNowOf = async (
  row: Pick<PreLead, "company_name" | "country" | "industry" | "domain">,
  offer: string,
): Promise<{
  fact: string;
  news: Awaited<ReturnType<typeof recentNews>>;
  signals: Awaited<ReturnType<typeof recentSignals>>;
}> => {
  // Bing answers a plain query only (no quotes, no topic terms): the name as given (Arabic names find Arabic
  // news) with a short country to pin generic names ("Ministry of Finance"), then the name alone when the country
  // starved it; the model then judges which dated fact matters.
  const name = row.company_name.split(" | ")[0].trim();
  const country =
    row.country === "United Arab Emirates" ? "UAE" : (row.country ?? "");
  const latin = name
    .replace(/[^ -~]+/g, " ")
    .replace(/\s+/g, " ")
    .trim(); // a mixed-script name finds nothing
  // read once, beside the news queries
  const reading = recentSignals({
    company: name,
    domain: row.domain ?? undefined,
    country: row.country ?? undefined,
    industry: row.industry ?? undefined,
  });
  const queries = [
    ...new Set([`${name} ${country}`.trim(), name, latin]),
  ].filter(Boolean);
  let items: Awaited<ReturnType<typeof recentNews>> = [];
  for (const q of queries) {
    items = await recentNews(q);
    if (items.length) break;
  }
  const signals = await reading;
  if (!items.length && !signals.length)
    return { fact: "", news: items, signals };
  const { why_now } = await runAiJson(
    "why_now",
    buildWhyNowPrompt({
      offer,
      company: {
        name: row.company_name,
        country: row.country,
        industry: row.industry,
      },
      news: items.map((i) => `${i.date}: ${i.title} (${i.source})`).join("; "),
      signals: signalsText(signals),
    }),
    WhyNowSchema,
  );
  return { fact: why_now, news: items, signals };
};

/**
 * Keep a pre-lead: a row without a why-now fact gets one from its news first (none found: the row is removed, with
 * that reason), then find the people to write to (stored as candidates, no CRM contact), pick the recipient, draft
 * touch 1 and mark it ready. A step that fails is logged on the row, which stays "kept".
 */
export const keepPreLead = async (
  dataProvider: CrmDataProvider,
  preLead: PreLead,
  input: {
    config: ConfigurationContextValue;
    identity: { fullName: string; email: string };
    defaultSender: Sender;
    offer: string;
    say?: (line: string) => void;
  },
): Promise<PreLead> => {
  let row = await patch(dataProvider, preLead, {
    status: "kept",
    closed_reason: null,
    closed_at: null,
    needs_reply: false, // the nightly run's "no named person" flag: Keep looks again
  });
  try {
    const { config, identity, say } = input;
    if (!row.why_now?.trim()) {
      say?.(`Reading the news about ${row.company_name}`);
      const { fact, news, signals } = await whyNowOf(row, input.offer);
      // what was read shows in the Evidence tab; none found keeps what the ranking read
      const evidence = {
        ...row.evidence,
        ...(news.length ? { news } : {}),
        ...(signals.length ? { signals } : {}),
      };
      if (!fact)
        return patch(dataProvider, row, {
          status: "closed",
          closed_reason: "removed",
          closed_at: now(),
          removed_reason: NO_WHY_NOW,
          evidence: { ...evidence, log: [...log(row), NO_WHY_NOW] },
        });
      row = await patch(dataProvider, row, {
        why_now: fact,
        checked: true,
        evidence,
      });
    }
    say?.(`Finding contacts at ${row.company_name}`);
    const notes: string[] = [];
    let site: Awaited<ReturnType<typeof lookupCompany>> | null = null;
    if (row.website)
      try {
        site = await lookupCompany(row.website);
      } catch (error) {
        notes.push((error as Error).message);
      }
    const text = site?.pages.map((p) => p.text).join("\n");
    const read = text ? { site_summary: siteExcerpt(text) } : {};
    // the row's own contact (added by hand, or found before) first: a tie in bestRecipient keeps it
    const found: FoundContact[] = [
      ...ownContact(row),
      ...(site?.emails ?? []).map(contactFromEmail),
    ];
    if (row.domain)
      try {
        found.push(
          ...(await findPeople(dataProvider, row.domain, row.country)),
        );
      } catch (error) {
        notes.push((error as Error).message);
      }
    const candidates = await usableContacts(found, {
      isSuppressed: (email) => dataProvider.isSuppressed(email),
      receivesMail,
    });
    const recipient = bestRecipient(candidates);
    const sender = senderOf(recipient, input.defaultSender);
    const mailbox =
      sender === "personal"
        ? identity.email
        : (config.outlook_send_as ?? DEFAULT_SEND_AS);
    if (!recipient) {
      row = await patch(dataProvider, row, {
        candidates,
        sender,
        // no one to write to: the row shows no stale person
        contact_first_name: null,
        contact_last_name: null,
        contact_title: null,
        contact_email: null,
        contact_linkedin_url: null,
        evidence: {
          ...row.evidence,
          ...read,
          log: [...notes, `No email address found for ${row.company_name}`],
        },
      });
      return row;
    }
    row = await patch(dataProvider, row, {
      candidates,
      contact_first_name: recipient?.first_name ?? null,
      contact_last_name: recipient?.last_name ?? null,
      contact_title: recipient?.title ?? null,
      contact_email: recipient?.email ?? null,
      contact_linkedin_url:
        (recipient as FoundContact | null)?.linkedin_url ?? null,
      sender,
      mailbox,
      signed_by: identity.fullName,
      evidence: { ...row.evidence, ...read },
    });

    say?.(`Drafting the first email to ${row.company_name}`);
    const brochures = config.lead_brochures ?? [];
    const ranked = rankedFromRow(row, recipient);
    const { draft, problem, draft_by, draft_prompt } = await writeDraft(
      (prompt, answered) => runAi("draft_email", prompt, answered),
      buildDraftPrompt({
        sender: identity.fullName || config.title || "THNK",
        company: config.title || "THNK",
        offer: input.offer,
        lead: ranked,
        leadSite: site?.pages,
        brochures,
      }),
      DraftSchema,
    );
    const attached = brochuresOf(draft, brochures).map((b) => b.name);
    const message = {
      pre_lead_id: row.id,
      direction: "out",
      touch: 1,
      status: "draft",
      mailbox,
      from_email: mailbox,
      to_email: recipient.email,
      subject: draft.subject,
      body: personaliseBody(
        draft.body,
        recipient,
        signatureFor(sender, identity.fullName, {
          personal: config.signature_personal,
          sales: config.signature_sales,
        }),
      ),
      attached,
      draft_by,
      draft_prompt,
      error: problem,
    };
    // a repeated Keep rewrites the touch-1 draft instead of adding a second one
    const { data: earlier } = await dataProvider.getList<PreLeadMessage>(
      "pre_lead_messages",
      {
        filter: {
          pre_lead_id: row.id,
          direction: "out",
          touch: 1,
          status: "draft",
        },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      },
    );
    if (earlier[0])
      await dataProvider.update("pre_lead_messages", {
        id: earlier[0].id,
        data: message,
        previousData: earlier[0],
      });
    else await dataProvider.create("pre_lead_messages", { data: message });
    row = await patch(dataProvider, row, {
      status: "ready",
      brochures: attached,
      evidence: { ...row.evidence, log: [] },
    });
  } catch (error) {
    row = await patch(dataProvider, row, {
      evidence: {
        ...row.evidence,
        log: [...log(row), (error as Error).message],
      },
    });
  }
  return row;
};

/** Write to another of the people found: the row's contact, and the unsent touch-1 draft addressed and greeted anew */
export const writeToCandidate = async (
  dataProvider: CrmDataProvider,
  row: PreLead,
  person: FoundContact,
  draft: PreLeadMessage | undefined,
  signature: string,
): Promise<void> => {
  await patch(dataProvider, row, {
    contact_first_name: person.first_name || null,
    contact_last_name: person.last_name || null,
    contact_title: person.title || null,
    contact_email: person.email,
    contact_linkedin_url: person.linkedin_url ?? null,
  });
  if (draft?.status === "draft")
    await dataProvider.update("pre_lead_messages", {
      id: draft.id,
      data: {
        to_email: person.email,
        body: personaliseBody(draft.body ?? "", person, signature),
      },
      previousData: draft,
    });
};

/** Close pre-leads as removed with the User's reason; the agent learns from them through recentRejections */
export const removePreLeads = async (
  dataProvider: CrmDataProvider,
  rows: PreLead[],
  reason: string,
): Promise<void> => {
  const closed_at = now();
  for (const row of rows)
    await patch(dataProvider, row, {
      status: "closed",
      closed_reason: "removed",
      closed_at,
      removed_reason: reason.trim() || null,
    });
};

/** The leads removed lately (latest 60) plus the older ones kept in Settings, for the agent's prompts */
export const recentRejections = async (
  dataProvider: CrmDataProvider,
  config: Pick<ConfigurationContextValue, "lead_rejections">,
): Promise<Rejection[]> => {
  const { data } = await dataProvider.getList<PreLead>("pre_leads", {
    filter: { closed_reason: "removed" },
    pagination: { page: 1, perPage: 60 },
    sort: { field: "closed_at", order: "DESC" },
  });
  return [...rejectionsFrom(data), ...(config.lead_rejections ?? [])];
};

export const reopenPreLead = async (
  dataProvider: CrmDataProvider,
  row: PreLead,
): Promise<void> => {
  await patch(dataProvider, row, {
    status: "found",
    closed_reason: null,
    closed_at: null,
  });
};

const dateOf = (m: PreLeadMessage) =>
  m.sent_at ?? m.received_at ?? m.created_at;

/** Turn a replied pre-lead into a CRM company, contact and deal, with its emails as deal notes */
export const convertPreLead = async (
  dataProvider: CrmDataProvider,
  row: PreLead,
  messages: PreLeadMessage[],
  input: {
    salesId?: Identifier;
    sectors?: { value: string; label?: string }[];
  },
): Promise<{ dealId: Identifier }> => {
  const replier = [...messages].reverse().find((m) => m.direction === "in");
  const [first, ...rest] = (replier?.from_name ?? "").trim().split(/\s+/);
  const email = replier?.from_email ?? row.contact_email ?? undefined;
  const first_name = replier?.from_name ? first : row.contact_first_name;
  const ref = row.ref ?? `pre_lead:${row.id}`;
  const source = (row.source ?? "wikidata") as RankedLead["lead"]["source"];
  const { deals } = await addLeads(
    [
      {
        ref,
        source,
        company: {
          name: row.company_name,
          website: row.website ?? undefined,
          domain: row.domain ?? undefined,
          city: row.city ?? undefined,
          country: row.country ?? undefined,
          linkedin_url: row.linkedin_url ?? undefined,
          industry: row.industry ?? undefined,
          employees: row.employees ?? undefined,
        },
        contact:
          first_name || email
            ? {
                first_name: first_name || email!,
                last_name: replier?.from_name
                  ? rest.join(" ")
                  : (row.contact_last_name ?? undefined),
                title: row.contact_title ?? undefined,
                email,
                linkedin_url: row.contact_linkedin_url ?? undefined,
              }
            : undefined,
      },
    ],
    dataProvider,
    {
      salesId: input.salesId,
      sectors: input.sectors,
      fits:
        row.fit == null
          ? {}
          : { [ref]: agentFit(row.fit, row.why ?? "", row.segment_name ?? "") },
    },
  );
  // a retry after a partial failure finishes the deal the first try made for this pre-lead: its company carries the
  // pre-lead's source and ref, and the deal is newer than the pre-lead (any other company with a deal still refuses)
  const earlierDeal = async () => {
    const { data: made } = await dataProvider.getList("companies", {
      filter: { lead_source: source, lead_source_ref: ref },
      pagination: one,
      sort: byId,
    });
    if (!made[0]) return undefined;
    const { data: deal } = await dataProvider.getList("deals", {
      filter: { company_id: made[0].id, "created_at@gte": row.created_at },
      pagination: one,
      sort: byId,
    });
    return deal[0]?.id;
  };
  const dealId = deals[ref] ?? row.converted_deal_id ?? (await earlierDeal());
  if (dealId == null)
    throw new Error(
      `${row.company_name} was not added: already a company with a deal, or on the do-not-contact list`,
    );
  // a retry skips the notes the first try wrote
  const { data: written } = await dataProvider.getList("deal_notes", {
    filter: { deal_id: dealId },
    pagination: { page: 1, perPage: 500 },
    sort: byId,
  });
  const texts = new Set(written.map((n) => n.text));
  // only what really went out or came in: never an unsent draft or a queued touch
  for (const m of messages.filter((m) =>
    ["sent", "received"].includes(m.status),
  )) {
    const text = `${m.direction === "out" ? "Sent" : "Received"} ${dateOf(m).slice(0, 10)} · ${m.subject ?? ""}\n\n${m.body ?? ""}`;
    if (texts.has(text)) continue;
    await dataProvider.create("deal_notes", {
      data: {
        deal_id: dealId,
        sales_id: input.salesId,
        date: dateOf(m),
        text,
      },
    });
  }
  const { data: deal } = await dataProvider.getOne("deals", { id: dealId });
  await dataProvider.outreach({
    action: "converted",
    pre_lead_id: row.id,
    deal_id: dealId,
    company_id: deal.company_id,
    contact_id: deal.contact_ids?.[0] ?? null,
  });
  return { dealId };
};
